import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Users, TrendingUp, Award, Target, Plus, Copy, Sparkles, Printer, Calendar, Filter } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { PromotionalCodeManager } from './PromotionalCodeManager';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend } from 'recharts';

const COLORS = [
  'hsl(var(--primary))',
  'hsl(210, 70%, 50%)',
  'hsl(150, 60%, 45%)',
  'hsl(280, 60%, 55%)',
  'hsl(30, 80%, 55%)',
  'hsl(0, 70%, 55%)',
];

export const AdminReferralsPanel = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [customCode, setCustomCode] = useState('');
  const [generatedCodes, setGeneratedCodes] = useState<string[]>([]);
  const [codeQuantity, setCodeQuantity] = useState('5');
  const [validityType, setValidityType] = useState<'indefinite' | 'range'>('indefinite');
  const [validFrom, setValidFrom] = useState('');
  const [validUntil, setValidUntil] = useState('');
  
  const { data: analytics, isLoading } = useQuery({
    queryKey: ['admin-referral-analytics'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('admin_get_referral_analytics');
      if (error) throw error;
      return data as any;
    }
  });

  const createCodeMutation = useMutation({
    mutationFn: async (code: string) => {
      const finalCode = code.toUpperCase().replace(/[^A-Z0-9-]/g, '');
      if (finalCode.length < 4) throw new Error(t('adminReferrals.codeMinLength'));
      
      const { data: existing } = await supabase
        .from('users')
        .select('referral_code')
        .eq('referral_code', finalCode)
        .single();
      
      if (existing) throw new Error(t('adminReferrals.codeExists'));
      
      return finalCode;
    },
    onSuccess: (code) => {
      setGeneratedCodes(prev => [code, ...prev]);
      setCustomCode('');
      toast.success(t('adminReferrals.codeCreatedSuccess', { code }));
    },
    onError: (error: Error) => {
      toast.error(error.message);
    }
  });

  const generateRandomCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let code = 'PETIUS-';
    for (let i = 0; i < 6; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return code;
  };

  const handleCreateCode = () => {
    const code = customCode.trim() || generateRandomCode();
    createCodeMutation.mutate(code);
  };

  const handleGenerateBulk = (count: number) => {
    const codes: string[] = [];
    for (let i = 0; i < count; i++) {
      codes.push(generateRandomCode());
    }
    setGeneratedCodes(prev => [...codes, ...prev]);
    toast.success(t('adminReferrals.bulkGenerated', { count }));
  };

  const handleGenerateCustomQuantity = () => {
    const count = parseInt(codeQuantity) || 5;
    if (count < 1 || count > 100) {
      toast.error(t('adminReferrals.quantityRange'));
      return;
    }
    handleGenerateBulk(count);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success(t('adminReferrals.copiedToClipboard'));
  };

  const copyAllCodes = () => {
    navigator.clipboard.writeText(generatedCodes.join('\n'));
    toast.success(t('adminReferrals.allCodesCopied'));
  };

  const handlePrint = () => {
    window.print();
  };
  
  if (isLoading) {
    return <Skeleton className="h-96" />;
  }
  
  const globalStats = analytics?.global_stats as any;
  const topReferrers = (analytics?.top_referrers || []) as any[];
  
  return (
    <div className="space-y-6 pb-24 print:p-4">
      {/* Print Button */}
      <div className="flex justify-end print:hidden">
        <Button variant="outline" size="sm" onClick={handlePrint}>
          <Printer className="h-4 w-4 mr-2" />
          {t('adminReferrals.print')}
        </Button>
      </div>

      {/* Promotional Codes Manager */}
      <div className="print:hidden">
        <PromotionalCodeManager />
      </div>

      {/* Code Generator Card */}
      <Card className="border-primary/20 bg-gradient-to-r from-primary/5 to-transparent print:hidden">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center justify-between flex-wrap gap-2">
            <span className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              {t('adminReferrals.marketingCodeGenerator')}
            </span>
            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
              <DialogTrigger asChild>
                <Button size="sm">
                  <Plus className="h-4 w-4 mr-1" />
                  {t('adminReferrals.createCode')}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>{t('adminReferrals.createReferralCode')}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>{t('adminReferrals.customCodeOptional')}</Label>
                    <Input
                      value={customCode}
                      onChange={(e) => setCustomCode(e.target.value.toUpperCase())}
                      placeholder={t('adminReferrals.codePlaceholder')}
                      className="mt-1 font-mono"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      {t('adminReferrals.leaveEmptyForRandom')}
                    </p>
                  </div>

                  {/* Validity Configuration */}
                  <div className="border-t pt-4">
                    <Label className="flex items-center gap-2 mb-3">
                      <Calendar className="h-4 w-4" />
                      {t('adminReferrals.validityPeriod')}
                    </Label>
                    <Select value={validityType} onValueChange={(v) => setValidityType(v as 'indefinite' | 'range')}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="indefinite">{t('adminReferrals.indefinite')}</SelectItem>
                        <SelectItem value="range">{t('adminReferrals.specificRange')}</SelectItem>
                      </SelectContent>
                    </Select>
                    
                    {validityType === 'range' && (
                      <div className="grid grid-cols-2 gap-2 mt-3">
                        <div>
                          <Label className="text-xs">{t('adminReferrals.validFrom')}</Label>
                          <Input
                            type="date"
                            value={validFrom}
                            onChange={(e) => setValidFrom(e.target.value)}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">{t('adminReferrals.validUntil')}</Label>
                          <Input
                            type="date"
                            value={validUntil}
                            onChange={(e) => setValidUntil(e.target.value)}
                            min={validFrom}
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex gap-2">
                    <Button onClick={handleCreateCode} className="flex-1" disabled={createCodeMutation.isPending}>
                      {createCodeMutation.isPending ? t('common.processing') : t('adminReferrals.createCode')}
                    </Button>
                  </div>
                  
                  <div className="border-t pt-4">
                    <p className="text-sm font-medium mb-2">{t('adminReferrals.bulkGenerate')}</p>
                    <div className="flex gap-2 flex-wrap">
                      <Button variant="outline" size="sm" onClick={() => handleGenerateBulk(5)}>
                        {t('adminReferrals.codes', { count: 5 })}
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleGenerateBulk(10)}>
                        {t('adminReferrals.codes', { count: 10 })}
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleGenerateBulk(25)}>
                        {t('adminReferrals.codes', { count: 25 })}
                      </Button>
                    </div>
                    
                    {/* Custom Quantity */}
                    <div className="flex gap-2 mt-3">
                      <Input
                        type="number"
                        value={codeQuantity}
                        onChange={(e) => setCodeQuantity(e.target.value)}
                        min="1"
                        max="100"
                        className="w-24"
                        placeholder="50"
                      />
                      <Button variant="outline" size="sm" onClick={handleGenerateCustomQuantity}>
                        {t('adminReferrals.generateCustom')}
                      </Button>
                    </div>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </CardTitle>
        </CardHeader>
        {generatedCodes.length > 0 && (
          <CardContent>
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-muted-foreground">
                {t('adminReferrals.codesGenerated', { count: generatedCodes.length })}
              </p>
              <Button variant="ghost" size="sm" onClick={copyAllCodes}>
                <Copy className="h-3 w-3 mr-1" />
                {t('adminReferrals.copyAll')}
              </Button>
            </div>
            <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
              {generatedCodes.map((code, idx) => (
                <Badge 
                  key={idx} 
                  variant="secondary" 
                  className="font-mono cursor-pointer hover:bg-primary/20"
                  onClick={() => copyToClipboard(code)}
                >
                  {code}
                  <Copy className="h-3 w-3 ml-1 opacity-50" />
                </Badge>
              ))}
            </div>
          </CardContent>
        )}
      </Card>

      {/* Global Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="print:border print:shadow-none">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Users className="h-4 w-4 text-blue-500" />
              {t('adminReferrals.totalUsersWithCode')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold">{globalStats?.total_users_with_code || 0}</p>
          </CardContent>
        </Card>
        
        <Card className="print:border print:shadow-none">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-green-500" />
              {t('adminReferrals.totalReferredUsers')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold">{globalStats?.total_referred_users || 0}</p>
          </CardContent>
        </Card>
        
        <Card className="print:border print:shadow-none">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Award className="h-4 w-4 text-yellow-500" />
              {t('adminReferrals.completedReferrals')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold">{globalStats?.total_completed_referrals || 0}</p>
          </CardContent>
        </Card>
        
        <Card className="print:border print:shadow-none">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Target className="h-4 w-4 text-purple-500" />
              {t('adminReferrals.conversionRate')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold">{globalStats?.conversion_rate || 0}%</p>
          </CardContent>
        </Card>
      </div>

      {/* Statistical Charts */}
      {(globalStats?.total_users_with_code > 0 || topReferrers.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 print:grid-cols-2">
          {/* Conversion Funnel Pie */}
          <Card className="print:border print:shadow-none print:break-inside-avoid">
            <CardHeader>
              <CardTitle className="text-base">{t('adminReferrals.conversionFunnel')}</CardTitle>
              <CardDescription>{t('adminReferrals.conversionFunnelDesc')}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[250px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={[
                        { name: t('adminReferrals.completedReferrals'), value: globalStats?.total_completed_referrals || 0, fill: COLORS[2] },
                        { name: t('adminReferrals.pendingReferrals'), value: Math.max(0, (globalStats?.total_referred_users || 0) - (globalStats?.total_completed_referrals || 0)), fill: COLORS[4] },
                      ]}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="value"
                      label={({ name, value }) => `${name}: ${value}`}
                    >
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Top Referrers Bar Chart */}
          {topReferrers.length > 0 && (
            <Card className="print:border print:shadow-none print:break-inside-avoid">
              <CardHeader>
                <CardTitle className="text-base">{t('adminReferrals.topReferrersChart')}</CardTitle>
                <CardDescription>{t('adminReferrals.topReferrersChartDesc')}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[250px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={topReferrers.slice(0, 5).map(r => ({
                        name: r.full_name?.split(' ')[0] || t('adminReferrals.anonymous'),
                        total: r.total_referrals,
                        completed: r.completed_referrals,
                      }))}
                      layout="vertical"
                      margin={{ left: 60 }}
                    >
                      <XAxis type="number" />
                      <YAxis type="category" dataKey="name" width={55} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="total" name={t('adminReferrals.total')} fill={COLORS[0]} />
                      <Bar dataKey="completed" name={t('adminReferrals.completed')} fill={COLORS[2]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}
      
      {/* Top Referrers Table */}
      <Card className="print:border print:shadow-none print:break-inside-avoid">
        <CardHeader>
          <CardTitle>{t('adminReferrals.topReferrers')}</CardTitle>
        </CardHeader>
        <CardContent>
          {topReferrers.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('adminReferrals.user')}</TableHead>
                  <TableHead>{t('adminReferrals.email')}</TableHead>
                  <TableHead>{t('adminReferrals.referralCode')}</TableHead>
                  <TableHead className="text-center">{t('adminReferrals.total')}</TableHead>
                  <TableHead className="text-center">{t('adminReferrals.completed')}</TableHead>
                  <TableHead className="text-center">{t('adminReferrals.creditsEarned')}</TableHead>
                  <TableHead>{t('adminReferrals.lastReferral')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {topReferrers.map((referrer: any) => (
                  <TableRow key={referrer.user_id}>
                    <TableCell className="font-medium">{referrer.full_name}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{referrer.email}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="font-mono">{referrer.referral_code}</Badge>
                    </TableCell>
                    <TableCell className="text-center font-bold">{referrer.total_referrals}</TableCell>
                    <TableCell className="text-center text-green-600">{referrer.completed_referrals}</TableCell>
                    <TableCell className="text-center text-yellow-600">{referrer.total_credits_earned}</TableCell>
                    <TableCell className="text-sm">
                      {referrer.last_referral_date ? new Date(referrer.last_referral_date).toLocaleDateString() : '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="text-center text-muted-foreground py-8">{t('adminReferrals.noReferrersYet')}</p>
          )}
        </CardContent>
      </Card>

      {/* Print Styles */}
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          .print\\:p-4, .print\\:p-4 * {
            visibility: visible;
          }
          .print\\:hidden {
            display: none !important;
          }
          .print\\:border {
            border: 1px solid #e5e7eb !important;
          }
          .print\\:shadow-none {
            box-shadow: none !important;
          }
        }
      `}</style>
    </div>
  );
};
